import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
    'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
    'textarea:not([disabled])', 'a[href]', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Enfocables reales del panel: descarta los que se sacaron del orden de
 * tabulación (``tabindex="-1"``), los ocultos por atributo y los ocultos por
 * la utilidad ``hidden`` (display:none), que jsdom no puede detectar por layout.
 */
const focusableWithin = (panel: HTMLElement): HTMLElement[] =>
    Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(el =>
        el.getAttribute('tabindex') !== '-1'
        && !el.hidden
        && el.getAttribute('aria-hidden') !== 'true'
        && !el.classList.contains('hidden'));

/** Devuelve el foco a ``element`` solo si sigue en el documento y es enfocable. */
const restoreFocus = (element: HTMLElement | null) => {
    if (!element || !element.isConnected || typeof element.focus !== 'function') return;
    try { element.focus(); } catch { /* elemento ya no enfocable: se deja donde esté */ }
};

interface ModalFocusOptions {
    /** El diálogo está montado y visible. Cambiarlo a ``false`` restaura el foco. */
    active: boolean;
    /** Contenedor del diálogo (``role="dialog"``, ``tabIndex={-1}``). */
    panelRef: RefObject<HTMLElement | null>;
    /** Control que recibe el foco inicial; si falta, el panel. */
    initialFocusRef?: RefObject<HTMLElement | null>;
    /** Se invoca al pulsar Escape con el diálogo activo. */
    onEscape: () => void;
}

/**
 * Comportamiento de foco de un diálogo modal:
 *  - al activarse guarda el elemento enfocado (opener) y mueve el foco dentro
 *    (``initialFocusRef`` o el panel) salvo que ya estuviera dentro;
 *  - Escape llama ``onEscape``; Tab/Shift+Tab ciclan dentro del panel y
 *    recuperan el foco si se escapó (p. ej. al desmontarse el elemento enfocado);
 *  - al desactivarse o desmontar devuelve el foco al opener si sigue en el DOM.
 *
 * El opener se captura solo cuando ``active`` pasa a ``true``: cambiar el
 * contenido con el diálogo abierto no mueve el foco ni pierde el opener.
 */
export const useModalFocus = ({ active, panelRef, initialFocusRef, onEscape }: ModalFocusOptions) => {
    useEffect(() => {
        if (!active) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const panel = panelRef.current;
        if (panel && !(opener && panel.contains(opener))) {
            (initialFocusRef?.current ?? panel).focus();
        }
        return () => { restoreFocus(opener); };
        // initialFocusRef/panelRef son refs estables: solo ``active`` decide.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(() => {
        if (!active) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') { event.preventDefault(); onEscape(); return; }
            if (event.key !== 'Tab') return;
            const panel = panelRef.current;
            if (!panel) return;
            const items = focusableWithin(panel);
            const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            if (items.length === 0) { event.preventDefault(); panel.focus(); return; }
            const first = items[0];
            const last = items[items.length - 1];
            if (!activeElement || !panel.contains(activeElement)) { event.preventDefault(); first.focus(); return; }
            if (event.shiftKey && activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [active, onEscape, panelRef]);
};
